import { OTHER_SPECIALTY_ID } from '@/components/ui/select/clinician-type-select';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { mapOrganisationSizeToClinicSize } from '@heidi/growth/contracts';
import { EHRProvider } from '@heidi/schema/ml-scribe';
import { countries, type CountryType } from '@heidi/utils/countries';
import { type UserResponse } from '@heidi/workspace/contracts';
import { useEffect, useRef } from 'react';

import type { PersonalizationFormData } from '../personalizationTypes';

type UpdateFormData = <K extends keyof PersonalizationFormData>(
  field: K,
  value: PersonalizationFormData[K]
) => void;

type SavedUserProfile = Pick<
  UserResponse,
  | 'first_name'
  | 'last_name'
  | 'jurisdiction'
  | 'phone_number'
  | 'ehr_provider'
  | 'organisation_info'
  | 'language_time_settings'
>;

const EHR_PROVIDERS: ReadonlySet<string> = new Set(Object.values(EHRProvider));

function isEhrProvider(value: string): value is EHRProvider {
  return EHR_PROVIDERS.has(value);
}

/**
 * The form fields a user already saved, for a resume after the
 * /auth/onboard steps. Only fields with a saved value are present, so spreading
 * the result never blanks a field. Country and specialty are not here: they
 * go through `useSeedFormFromSavedUser`, which resolves them against lists.
 */
export function buildSeedFromSavedUser(
  user: SavedUserProfile
): Partial<PersonalizationFormData> {
  const seed: Partial<PersonalizationFormData> = {};
  const organisation = user.organisation_info;
  const clinicSize = mapOrganisationSizeToClinicSize(organisation?.size);
  const displayLanguage = user.language_time_settings?.display_language;

  if (user.first_name) seed.firstName = user.first_name;
  if (user.last_name) seed.lastName = user.last_name;
  if (organisation?.role) seed.role = organisation.role;
  if (user.jurisdiction) seed.jurisdiction = user.jurisdiction;
  if (user.phone_number) seed.phoneNumber = user.phone_number;
  if (organisation?.name) seed.clinicName = organisation.name;
  if (clinicSize) seed.clinicSize = clinicSize;
  if (user.ehr_provider && isEhrProvider(user.ehr_provider)) {
    seed.emrEhr = user.ehr_provider;
  }
  if (displayLanguage) seed.displayLanguage = displayLanguage;
  return seed;
}

/**
 * Preselect the form from the user's saved values instead of the geo-guessed
 * default. On-prem the appliance country is stamped on team-join, so a user
 * entering onboarding often already has a country (and possibly a specialty)
 * that should show as selected. Seed once each; only when the user has a
 * value — never clobber an in-progress selection or overwrite with empty, so
 * a fresh signup (no saved values) keeps the geo default. Country goes through
 * `handleCountryChange` so the region/language stay consistent and the geo
 * upgrade won't override it (it marks the country as user-set).
 *
 * The country seed runs before the specialty seed (declaration order), so the
 * flag-gated specialty reset inside `handleCountryChange` never clobbers a
 * seeded specialty. When resuming after the /auth/onboard steps, the saved
 * jurisdiction and display language are re-applied right after the country
 * seed, because `handleCountryChange` resets both.
 */
export function useSeedFormFromSavedUser(
  updateFormData: UpdateFormData,
  handleCountryChange: (
    value: CountryType | null,
    options?: { isSeed?: boolean }
  ) => void,
  options: { resumeAfterAuthOnboard?: boolean } = {}
) {
  const { resumeAfterAuthOnboard = false } = options;
  const { data: user } = useQueryUser();
  const { data: specialties } = useQuerySpecialties((data) => data.specialties);
  const hasSeededCountryRef = useRef(false);
  const hasSeededSpecialtyRef = useRef(false);

  useEffect(() => {
    if (hasSeededCountryRef.current || !user?.country) return;
    const match = countries.find((c) => c.name === user.country);
    if (match) {
      // isSeed exempts the emrEhr clear: the seed re-establishes
      // the country already in effect when the EHR was picked, and unlike
      // jurisdiction/displayLanguage nothing re-applies emrEhr afterwards.
      handleCountryChange(match, { isSeed: true });
      hasSeededCountryRef.current = true;
      if (resumeAfterAuthOnboard) {
        const { jurisdiction, displayLanguage } = buildSeedFromSavedUser(user);
        if (jurisdiction) updateFormData('jurisdiction', jurisdiction);
        if (displayLanguage) updateFormData('displayLanguage', displayLanguage);
      }
    }
  }, [user, handleCountryChange, resumeAfterAuthOnboard, updateFormData]);

  useEffect(() => {
    if (hasSeededSpecialtyRef.current || !user) return;
    if (user.specialty_id) {
      if (!specialties) return;
      const match = specialties.find((s) => s.id === user.specialty_id);
      if (match) {
        updateFormData('specialty', match);
        hasSeededSpecialtyRef.current = true;
      }
    } else if (user.other_specialty) {
      updateFormData('specialty', {
        id: OTHER_SPECIALTY_ID,
        name: 'Other',
        group: { id: 'other', name: 'Other' },
      });
      updateFormData('otherSpecialty', user.other_specialty);
      hasSeededSpecialtyRef.current = true;
    }
  }, [user, specialties, updateFormData]);
}
